// AccessRestricted — the visible fallback for denied capabilities.
// Replaces silent <Navigate> redirects that left the user unable to tell
// whether the app was broken or they lacked permission (the /dashboard/team
// defect that motivated the 2026-02-21 route-permission overhaul).
import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess, minRolesFor } from "@/lib/routePermissions";

export function AccessRestricted({ capability, subject, backTo = "/dashboard", backLabel = "Back to dashboard" }) {
    const { employer } = useAuth();
    const role = employer?.team_role || "unknown";
    const minRoles = capability ? minRolesFor(capability) : ["ACCOUNT_ADMIN"];
    const readableSubject = (subject || (capability?.split(".")[0] || "this")).replace(/_/g, " ");
    const adminEmail = employer?.email || "your account admin";

    return (
        <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="access-restricted">
            <div className="max-w-lg w-full bg-card border border-border rounded-md p-8 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-800 mb-4">
                    <Lock size={26} weight="fill" />
                </div>
                <h1 className="font-serif text-2xl mb-2" data-testid="access-restricted-title">
                    Access restricted
                </h1>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Your role (<strong className="font-mono not-italic text-foreground">{role}</strong>){" "}
                    doesn&apos;t include access to <strong>{readableSubject}</strong>.
                </p>
                <p className="text-xs text-muted-foreground mb-6">
                    This screen is available to:{" "}
                    <span className="font-mono text-foreground">{minRoles.join(", ")}</span>
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                        to={backTo}
                        data-testid="access-restricted-back-btn"
                        className="inline-flex items-center gap-2 border border-border bg-background hover:bg-muted text-sm px-4 py-2 rounded-sm transition-all"
                    >
                        <ArrowLeft size={14} weight="bold" /> {backLabel}
                    </Link>
                    <a
                        href={`mailto:${adminEmail}?subject=Access%20request%3A%20${encodeURIComponent(readableSubject)}`}
                        data-testid="access-restricted-request-btn"
                        className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm hover:-translate-y-[1px] hover:shadow transition-all"
                    >
                        <EnvelopeSimple size={14} weight="fill" /> Ask your admin
                    </a>
                </div>
                {capability && (
                    <div className="mt-6 text-[10px] font-mono text-muted-foreground opacity-60">
                        capability: {capability}
                    </div>
                )}
            </div>
        </div>
    );
}

export function useCanAccess(capability) {
    const { employer } = useAuth();
    return canAccess(employer?.team_role, capability);
}

export function RequireCapability({ capability, subject, backTo, children }) {
    const allowed = useCanAccess(capability);
    if (!allowed) {
        return <AccessRestricted capability={capability} subject={subject} backTo={backTo} />;
    }
    return children;
}
